import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import Responsive from '../../../../../ui/utils/Responsive';
import {
	StyledBookingDetailsCell as Cell,
	StyledBookingDetailsRow as Row,
	StyledBookingDetailsTable as Table,
	StyledBookingDetailsTableBody as TableBody,
} from '../../booking/BookingDetailsTable';
import { useTranslation } from '../../../../shared/i18n/translate/useTranslation';
import { getFormattedPrice } from '../../../../shared/i18n/price/utils/getFormattedPrice';
import { getDifference } from '../../../../shared/i18n/price/utils/getDifference';
import { IPrice } from '../../../../shared/i18n/price/IPrice';
import { IBooking } from '../../../domain/booking/models/IBooking';
import { NewBooking } from '../../../domain/booking/models/NewBooking';
import { getOriginalGrandTotalPrice } from '../../../domain/booking/models/helpers/getOriginalGrandTotalPrice';
import { BookingPaymentMethod } from '../../booking/BookingPaymentMethod';
import { trackBookingCanceled } from '../shared/analytics/e-commerce/trackBookingCanceled';
import { trackBookingComplete } from '../shared/analytics/e-commerce/trackBookingComplete';

interface IConfirmationPaymentRefundProps {
	originalBooking: IBooking;
	booking?: NewBooking;
	shouldPay?: boolean;
}

export const ConfirmationPaymentRefund = ({
	booking,
	originalBooking,
	shouldPay,
}: IConfirmationPaymentRefundProps) => {
	const { t } = useTranslation();

	const originalTotalPrice = getOriginalGrandTotalPrice(originalBooking);

	useEffect(() => {
		trackBookingCanceled(originalBooking);

		if (booking) {
			trackBookingComplete(booking);
		}
	}, []);

	return (
		<CustomTable>
			<TableBody>
				{shouldPay ? (
					booking && (
						<NewBookingPayment
							booking={booking}
							amountDue={getDifference(booking.totalPrice, originalTotalPrice)}
						/>
					)
				) : (
					<>
						<Row>
							<Cell light>
								{t('parking_booking_cancel_amend_confirmation_total_refund')}
								{': '}
							</Cell>
							<Cell bold>
								{getFormattedPrice(
									booking
										? getDifference(originalTotalPrice, booking.totalPrice)
										: originalTotalPrice,
								)}
							</Cell>
						</Row>
						<Row>
							<Cell light>
								{t('parking_booking_cancel_amend_confirmation_refunded_to')}
								{': '}
							</Cell>
							<Cell bold>
								<BookingPaymentMethod
									paymentDetails={originalBooking.paymentDetails}
								/>
							</Cell>
						</Row>
					</>
				)}
			</TableBody>
		</CustomTable>
	);
};

interface INewBookingProps {
	booking: NewBooking;
	amountDue: IPrice;
}

const NewBookingPayment = ({ booking, amountDue }: INewBookingProps) => {
	const { t } = useTranslation();

	return (
		<>
			<Row>
				<Cell light>
					{t('parking_booking_cancel_amend_confirmation_total_payment')}
					{': '}
				</Cell>
				<Cell bold>{getFormattedPrice(amountDue)}</Cell>
			</Row>
			{booking.paymentDetails && (
				<Row>
					<Cell light>
						{t('parking_booking_cancel_amend_confirmation_payment_done')}
						{': '}
					</Cell>
					<Cell bold>
						<BookingPaymentMethod paymentDetails={booking.paymentDetails} />
					</Cell>
				</Row>
			)}
		</>
	);
};

const CustomTable = styled(Table)`
	margin-top: 40px;
	margin-bottom: 40px;

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		margin-top: 24px;
		margin-bottom: 24px;
	}

	${Cell} {
		vertical-align: bottom;

		&:last-of-type {
			text-align: right;
		}
	}
`;
